import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle, Clock, User, Save, Plus, Tags } from 'lucide-react';
import { useAdminTicket } from '../hooks';
import {
  TicketMessageList,
  TicketMessageInput,
  CannedResponsePicker,
  CustomerContextSidebar,
  TagBadge,
} from '../components';
import { timeAgo, formatFullDate } from '../utils/timeAgo';
import { ARCHE_PATH } from '../constants';
import api from '@/lib/api';
import type { SupportCannedResponseResponse, SupportTagResponse } from '@/lib/api';

export function AdminTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { ticket, messages, loading, error, addMessage, deleteMessage, refresh } =
    useAdminTicket(ticketId);

  const [editedStatus, setEditedStatus] = useState<string | null>(null);
  const [editedPriority, setEditedPriority] = useState<string | null>(null);
  const [editedAssignee, setEditedAssignee] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [cannedResponses, setCannedResponses] = useState<SupportCannedResponseResponse[]>([]);

  // Tag state
  const [allTags, setAllTags] = useState<SupportTagResponse[]>([]);
  const [ticketTags, setTicketTags] = useState<SupportTagResponse[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);

  useEffect(() => {
    Promise.all([api.listSupportCannedResponses(), api.listSupportTags()])
      .then(([cannedResult, tagsResult]) => {
        setCannedResponses(cannedResult.responses);
        setAllTags(tagsResult.tags);
      })
      .catch(() => {
        // Non-critical
      });
  }, []);

  // Set ticket tags when ticket loads
  useEffect(() => {
    if (ticket?.tags) {
      setTicketTags(ticket.tags);
    }
  }, [ticket?.tags]);

  const currentStatus = editedStatus ?? ticket?.status ?? '';
  const currentPriority = editedPriority ?? ticket?.priority ?? '';
  const currentAssignee = editedAssignee ?? ticket?.assignedTo ?? '';

  const hasChanges =
    (editedStatus !== null && editedStatus !== ticket?.status) ||
    (editedPriority !== null && editedPriority !== ticket?.priority) ||
    (editedAssignee !== null && editedAssignee !== (ticket?.assignedTo ?? ''));

  const handleSaveChanges = async () => {
    if (!ticketId || !hasChanges) return;
    setSaving(true);
    try {
      await api.updateSupportTicketAdmin(ticketId, {
        ...(editedStatus !== null && editedStatus !== ticket?.status
          ? { status: editedStatus }
          : {}),
        ...(editedPriority !== null && editedPriority !== ticket?.priority
          ? { priority: editedPriority }
          : {}),
        ...(editedAssignee !== null && editedAssignee !== (ticket?.assignedTo ?? '')
          ? { assignedTo: editedAssignee || null }
          : {}),
      });
      setEditedStatus(null);
      setEditedPriority(null);
      setEditedAssignee(null);
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const aiPriorityLabel = (p: string | null) => {
    if (!p) return 'Not analyzed';
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  const handleSendMessage = async (content: string) => {
    await addMessage(content, isInternalNote);
    setIsInternalNote(false);
  };

  const handleCannedResponse = (response: string) => {
    const inputEvent = new CustomEvent('canned-response-selected', {
      detail: { content: response },
    });
    window.dispatchEvent(inputEvent);
  };

  const handleAddTag = async (tagId: string) => {
    if (!ticketId) return;
    try {
      const result = await api.assignSupportTicketTags(ticketId, [tagId]);
      setTicketTags(result.tags);
      setShowTagPicker(false);
    } catch {
      // Silently fail — tag may already be assigned
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!ticketId) return;
    try {
      await api.removeSupportTicketTag(ticketId, tagId);
      setTicketTags((prev) => prev.filter((t) => t.tagId !== tagId));
    } catch {
      // Silently fail
    }
  };

  // Tags not yet assigned to this ticket
  const availableTags = allTags.filter((tag) => !ticketTags.some((tt) => tt.tagId === tag.tagId));

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Ticket not found'}</p>
            <Link to={`${ARCHE_PATH}/admin`} className="mt-4 inline-block">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Admin
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Back navigation */}
      <Link to={`${ARCHE_PATH}/admin`}>
        <Button variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Dashboard
        </Button>
      </Link>

      {/* Main content + sidebar layout */}
      <div className="flex gap-6">
        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Ticket Header with editable fields */}
          <Card>
            <CardHeader>
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-xl">{ticket.subject}</CardTitle>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div
                        className="flex items-center gap-1"
                        title={formatFullDate(ticket.createdAt)}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        Created {timeAgo(ticket.createdAt)}
                      </div>
                      {ticket.updatedAt !== ticket.createdAt && (
                        <div
                          className="flex items-center gap-1"
                          title={formatFullDate(ticket.updatedAt)}
                        >
                          Updated {timeAgo(ticket.updatedAt)}
                        </div>
                      )}
                      {ticket.userId && (
                        <div className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {ticket.userId}
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline">#{ticket.ticketId}</Badge>
                </div>

                {/* Editable Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={currentStatus} onValueChange={setEditedStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Priority</Label>
                    <Select value={currentPriority} onValueChange={setEditedPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Assignee</Label>
                    <Input
                      value={currentAssignee}
                      onChange={(e) => setEditedAssignee(e.target.value)}
                      placeholder="Unassigned"
                    />
                  </div>
                </div>

                {ticket.aiPriority && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">AI Priority:</span>
                    <Badge
                      variant="outline"
                      className="text-violet-700 border-violet-300 dark:text-violet-300 dark:border-violet-700"
                    >
                      {aiPriorityLabel(ticket.aiPriority)}
                    </Badge>
                  </div>
                )}

                {/* Tags */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                    <Label className="text-xs text-muted-foreground">Tags</Label>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {ticketTags.map((tag) => (
                      <TagBadge
                        key={tag.tagId}
                        name={tag.name}
                        color={tag.color}
                        onRemove={() => handleRemoveTag(tag.tagId)}
                      />
                    ))}
                    {showTagPicker ? (
                      <div className="flex items-center gap-1">
                        <Select onValueChange={(val) => handleAddTag(val)}>
                          <SelectTrigger className="h-7 w-[160px] text-xs">
                            <SelectValue placeholder="Select tag..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTags.map((tag) => (
                              <SelectItem key={tag.tagId} value={tag.tagId}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: tag.color }}
                                  />
                                  {tag.name}
                                </div>
                              </SelectItem>
                            ))}
                            {availableTags.length === 0 && (
                              <div className="px-2 py-1 text-xs text-muted-foreground">
                                No more tags available
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => setShowTagPicker(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setShowTagPicker(true)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add tag
                      </Button>
                    )}
                  </div>
                </div>

                {hasChanges && (
                  <div className="flex justify-end">
                    <Button onClick={handleSaveChanges} disabled={saving} size="sm">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{ticket.description}</p>
            </CardContent>
          </Card>

          {/* Message Thread */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Conversation</h2>
            <TicketMessageList messages={messages} isAdmin onDeleteMessage={deleteMessage} />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      className="rounded border-muted-foreground"
                    />
                    <span
                      className={
                        isInternalNote ? 'text-amber-600 font-medium' : 'text-muted-foreground'
                      }
                    >
                      Internal note
                    </span>
                  </label>
                </div>
                <CannedResponsePicker responses={cannedResponses} onSelect={handleCannedResponse} />
              </div>
              <TicketMessageInput
                onSend={handleSendMessage}
                placeholder={isInternalNote ? 'Write an internal note...' : 'Reply to customer...'}
                cannedResponses={cannedResponses}
              />
            </div>
          </div>
        </div>

        {/* Customer Context Sidebar */}
        {ticket.userId && (
          <div className="w-[280px] flex-shrink-0 hidden lg:block">
            <div className="sticky top-6">
              <CustomerContextSidebar userId={ticket.userId} currentTicketId={ticket.ticketId} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
